import { useEffect, useState } from "react";
import { Camera, Images, FolderOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { isNativeApp } from "@/lib/native/platform";
import {
  NATIVE_PICKER_REQUEST_EVENT,
  type NativePickerRequest,
} from "@/lib/native/fileInputBridge";
import type { NativePickSource } from "@/lib/native/media";

/**
 * Source chooser for the native file-input bridge.
 *
 * Renders nothing on the web. Inside the native shell it listens for the
 * bridge's request event and lets the user pick camera / photo library / files.
 */
export const NativeFilePickerSheet = () => {
  const [request, setRequest] = useState<NativePickerRequest | null>(null);

  useEffect(() => {
    if (!isNativeApp()) return;
    const handler = (event: Event) => {
      const custom = event as CustomEvent<NativePickerRequest>;
      if (!custom.detail) return;
      // Signals the bridge that a chooser is mounted and will resolve.
      custom.preventDefault();
      setRequest(custom.detail);
    };
    window.addEventListener(NATIVE_PICKER_REQUEST_EVENT, handler as EventListener);
    return () => window.removeEventListener(NATIVE_PICKER_REQUEST_EVENT, handler as EventListener);
  }, []);

  const choose = (source: NativePickSource | null) => {
    request?.resolve(source);
    setRequest(null);
  };

  if (!request) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && choose(null)}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Add {request.multiple ? "files" : "a file"}</DialogTitle>
          <DialogDescription>Choose where to take it from.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Button variant="outline" className="justify-start" onClick={() => choose("camera")}>
            <Camera className="mr-2 h-4 w-4" />
            Take photo
          </Button>
          <Button variant="outline" className="justify-start" onClick={() => choose("photos")}>
            <Images className="mr-2 h-4 w-4" />
            Photo library
          </Button>
          {!request.imageOnly && (
            <Button variant="outline" className="justify-start" onClick={() => choose("files")}>
              <FolderOpen className="mr-2 h-4 w-4" />
              Browse files
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NativeFilePickerSheet;
